<template>
  <div>
    <tool-bar :title="toolbarTitle"></tool-bar>
    <loading :loading="loading"></loading>

    <error-alert-item
      :error-alert-event="errorAlertEvent"
      :network-error="networkError"
      @resetErrorAlertEvent="errorAlertEvent=false"
    ></error-alert-item>

    <activity-redirect-item
      :loan-tasks="tasks"
      :current-loan-task="currentTaskName"
      :is-loan-task="true"
      :redirect-activity="redirectActivity"
    ></activity-redirect-item>

    <stage-comment-item></stage-comment-item>

    <refresh-item
      :show="showRefreshItem"
      @onRefresh="fetchEmiSchemes"
    ></refresh-item>

    <div v-if="emiPresent" class="margin_top_20">
      <div class="item-heading-desc-div margin-right-10 margin-left-10">
        <weex-text class="item-heading">Choose from Available EMI Schemes</weex-text>
      </div>

      <div class="item-row-alignment-center margin-left-10 margin-right-10">
        <div v-if="showBrand" class="loan-details-item-style horizontal_center">
          <weex-text class="product-details-item-label">Brand</weex-text>
          <weex-text class="product-details-item-value">{{getBrand}}</weex-text>
        </div>
        <div v-if="showModel" class="loan-details-item-style horizontal_center">
          <weex-text class="product-details-item-label">Product</weex-text>
          <weex-text class="product-details-item-value">{{getModel}}</weex-text>
        </div>
        <div v-if="getPrice" class="loan-details-item-style horizontal_center">
          <weex-text class="product-details-item-label">Price</weex-text>
          <weex-text class="product-details-item-value">{{getPrice}}</weex-text>
        </div>
      </div>
      <div>
        <div v-for="emiScheme in emiSchemes" :key="emiScheme.emiId">
          <emi-scheme-item
            class="margin-20"
            :emi-scheme="emiScheme"
            @onEmiSchemeChosen="emiSchemeChosen"
          ></emi-scheme-item>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ToolBar from "../../layout/ToolBar";
import Loading from "../../layout/Loading";
import EmiSchemeItem from "../../layout/EmiSchemeItem";
import ErrorAlertItem from "../../layout/ErrorAlertItem";
import StageCommentItem from "../../layout/StageCommentItem";
import ActivityRedirectItem from "../../layout/ActivityRedirectItem";
import EzUtils from "../../../utils/EzUtils";
import { DataTables } from "../../../utils/DataTables";
import { Activities } from "../../../utils/ActivityNavigator";
import DateUtils, { DateFormats } from "../../../utils/DateUtils";
import { MessageConstants } from "../../../utils/MessageConstantUtil";
import LoanTaskActivityNavigator from "../../../utils/LoanTasksActivityNavigator";
import DataSource from "../../../data/DataSource";
import LoanScopeDataHolder from "../../../data/model/LoanScopeDataHolder";
import AlertService from "../../core/alert/AlertService";
import RefreshItem from "../../layout/RefreshItem";
import storageUtil from "../../../utils/StorageUtil";

const STAR = "*";
const RUPEE = "\u20B9";

export default {
  name: "ChooseEmiActivity",
  components: {
    RefreshItem,
    ActivityRedirectItem,
    ErrorAlertItem,
    Loading,
    ToolBar,
    EmiSchemeItem,
    StageCommentItem
  },
  data: () => ({
    toolbarTitle: "EMI Schemes",
    showRefreshItem: false,
    emiPresent: false,
    emiSchemes: [],
    loading: false,
    errorAlertEvent: false,
    networkError: {}
  }),

  methods: {
    showError(error) {
      this.loading = false;
      this.networkError = error;
      this.errorAlertEvent = true;
    },

    showToast(message) {
      AlertService.showToast(message);
    },

    emiSchemeChosen(emiScheme) {
      LoanScopeDataHolder.setChosenEmiScheme(emiScheme);
      if (LoanScopeDataHolder.getChosenEmiScheme().loanProduct == null) {
        this.saveEmiScheme();
      } else {
        this.updateEmiScheme();
      }
    },

    saveEmiScheme() {
      this.loading = true;
      DataSource.saveEMiScheme(LoanScopeDataHolder.getChosenEmiScheme())
        .then(emiSaveRes => {
          if (emiSaveRes.errorOccurred) {
            this.showError(emiSaveRes.data);
          } else {
            DataSource.getLoanProductById(emiSaveRes.data.mifosLoanProductId)
              .then(loanProductRes => {
                if (loanProductRes.errorOccurred) {
                  this.showError(loanProductRes.data);
                } else {
                  const emiScheme = LoanScopeDataHolder.getChosenEmiScheme();
                  emiScheme.loanProduct = loanProductRes.data;
                  LoanScopeDataHolder.setChosenEmiScheme(emiScheme);
                  this.updateEmiScheme();
                }
              })
          }
        })
    },

    updateEmiScheme() {
      this.loading = true;
      const loansPayload = this.createLoansPayload();
      DataSource.updateLoansAccount(LoanScopeDataHolder.getLoanId(), loansPayload)
        .then(updateLoansRes => {
          if (updateLoansRes.errorOccurred) {
            this.showError(updateLoansRes.data);
          } else {
            this.completeCurrentTaskAndGetNextTask();
          }
        })
    },

    createLoansPayload() {
      const loansPayload = {};
      const chosenEMIScheme = LoanScopeDataHolder.getChosenEmiScheme();
      const loanProduct = chosenEMIScheme.loanProduct;

      loansPayload.productId = loanProduct.id;
      loansPayload.clientId = LoanScopeDataHolder.getClientId();

      if (loanProduct.charges != null) {
        const charges = [];
        for (let index in loanProduct.charges) {
          const charge = loanProduct.charges[index];
          const chargePayload = {};
          chargePayload.amount = charge.amount.toString();
          chargePayload.chargeId = charge.id;
          charges.push(chargePayload);
        }
        loansPayload.charges = charges;
      }

      const loanToValue = loanProduct.numberOfRepayments / (loanProduct.numberOfRepayments
        + chosenEMIScheme.downPayment);
      const loanAmount = loanToValue * LoanScopeDataHolder.getPhonePrice();

      if (chosenEMIScheme.downPayment < 0) {
        loansPayload.principal = LoanScopeDataHolder.getLoanAmount();
      } else {
        loansPayload.principal = loanAmount.toString();
      }

      loansPayload.loanTermFrequency = loanProduct.numberOfRepayments;

      const loanTermFrequencyType = loanProduct.repaymentFrequencyType.id;
      loansPayload.loanTermFrequencyType = loanTermFrequencyType;
      loansPayload.locale = "en";
      loansPayload.dateFormat = "dd MMMM yyyy";
      loansPayload.numberOfRepayments = loanProduct.numberOfRepayments.toString();
      loansPayload.repaymentEvery = loanProduct.repaymentEvery;
      loansPayload.repaymentFrequencyType = loanTermFrequencyType;
      loansPayload.interestRatePerPeriod = loanProduct.interestRatePerPeriod;
      loansPayload.amortizationType = loanProduct.amortizationType.id;
      loansPayload.interestType = loanProduct.interestType.id;
      loansPayload.interestCalculationPeriodType = loanProduct.interestCalculationPeriodType.id;
      loansPayload.transactionProcessingStrategyId = loanProduct.transactionProcessingStrategyId;
      loansPayload.allowPartialPeriodInterestCalcualtion = loanProduct.allowPartialPeriodInterestCalcualtion;
      loansPayload.expectedDisbursementDate =
        DateUtils.getCurrentTimeAsFormat(DateFormats.DD_MMMM_YYYY);

      return loansPayload;
    },

    completeCurrentTaskAndGetNextTask() {
      this.loading = true;
      LoanTaskActivityNavigator.getNextTask(this.tasks)
        .then(res => {
          this.loading = false;
          if (res.errorOccurred) {
            this.showError(res.data);
          }
        });
    },

    fetchEmiSchemes() {
      this.showRefreshItem = false;
      this.loading = true;
      DataSource.getDataTableInfo(
        DataTables.LOAN_DETAILS,
        LoanScopeDataHolder.getLoanId()
      )
        .then(loanDetailsRes => {
          if (loanDetailsRes.errorOccurred) {
            this.showError(loanDetailsRes.data);
            this.showRefreshItem = true;
          } else {
            LoanScopeDataHolder.setDetailsFromLoanDetails(loanDetailsRes.data[0]);
            DataSource.getDataTableInfo(
              DataTables.PRODUCT_DETAILS,
              LoanScopeDataHolder.getLoanId()
            )
              .then(productDetailsRes => {
                if (productDetailsRes.errorOccurred) {
                  this.showError(productDetailsRes.data);
                  this.showRefreshItem = true;
                } else {
                  if (productDetailsRes.data && EzUtils.isNotEmpty(productDetailsRes.data)) {
                    const productDetails = productDetailsRes.data[0];
                    if (productDetails.brand || productDetails.model) {
                      LoanScopeDataHolder.setProductCategory(productDetails.category);
                      LoanScopeDataHolder.setBrand(productDetails.brand);
                      LoanScopeDataHolder.setModel(productDetails.model);
                    }
                  }
                  DataSource.getEmiSchemes(
                    LoanScopeDataHolder.getClientId(),
                    "",
                    LoanScopeDataHolder.getProductCategory(),
                    LoanScopeDataHolder.getBrand(),
                    LoanScopeDataHolder.getModel(),
                    LoanScopeDataHolder.getPhonePrice(),
                    LoanScopeDataHolder.getLoanOfficeId(), // office id from prefManager for agent flow
                    "",
                    LoanScopeDataHolder.getLoanId()
                  )
                    .then(emiSchemesRes => {
                      this.loading = false;
                      if (emiSchemesRes.errorOccurred) {
                        this.showError(emiSchemesRes.data);
                        this.showRefreshItem = true;
                      } else {
                        if (EzUtils.isNotEmpty(emiSchemesRes.data)) {
                          this.emiPresent = true;
                          this.emiSchemes = emiSchemesRes.data;
                        } else {
                          //todo show dialog no emi scheme available
                          this.showToast(MessageConstants.EMI_SCHEMES_NOT_AVAILABLE_MESSAGE)
                        }
                      }
                    })
                }
              })
          }
        })
    }
  },

  created: function () {
    this.fetchEmiSchemes();
  },

  computed: {
    redirectActivity() {
      return Activities.LOAN_DETAILS;
    },

    tasks() {
      return [Activities.CHOOSE_EMI];
    },

    currentTaskName() {
      return LoanScopeDataHolder.getCurrentLoanTask()
        ? LoanScopeDataHolder.getCurrentLoanTask().name
        : "";
    },

    showBrand() {
      return this.getBrand && STAR !== this.getBrand;
    },

    getBrand() {
      return LoanScopeDataHolder.getBrand();
    },

    showModel() {
      return this.getModel && STAR !== this.getModel;
    },

    getModel() {
      return LoanScopeDataHolder.getModel();
    },

    getPrice() {
      return RUPEE + Number(LoanScopeDataHolder.getPhonePrice()).toFixed(0);
    },
  }
}
</script>

<style scoped src="../../../assets/style/base.css"></style>

<style scoped>

.product-details-item-label {
  color: gray;
  text-align: center;
  font-size: 14px
}

.product-details-item-value {
  text-align: center;
  font-size: 12px;
  color: blue;
}

.loan-details-item-style {
  width: 32%;
  background: #f9f9f9;
  padding: 5px 10px;
  box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.2), 0 5px 5px 0 rgba(0, 0, 0, 0.24);
}
</style>
